import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RefreshableOutlet from '../components/RefreshableOutlet';
import Swal from '../theme/swal';
import authService from '../services/authService';
import { getPrimaryTabs, getMoreLinks } from './nav/navConfig';
import MobileHeader from './components/MobileHeader';
import BottomNav from './components/BottomNav';
import MoreSheet from './components/MoreSheet';
import MobilePaiementSheet from './components/MobilePaiementSheet';
import MobileNotesSheet from './components/MobileNotesSheet';
import ConnectivityFooter from '../offline/ConnectivityFooter';
import KI from '../theme/palette';
import './mobile-theme.css';

const THEME_KEY = 'ki-mobile-theme';

/**
 * Native-app style shell used on phones (< md). Top app bar, scrollable
 * content, fixed bottom tab bar, and a slide-up "More" sheet. Renders the
 * same routed <Outlet /> as the desktop MainLayout, so routing/auth are shared.
 */
export default function MobileLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  // Sheet Achat/Paiement : null = fermé, sinon l'étape d'ouverture directe
  // ('type' = Type de paiement, 'achat' = Type d'achat) — owner Studio 2026-08-14.
  const [paySheet, setPaySheet] = useState(null);
  // Notes (discussion entre comptes) — sheet + unread count for the ⋮ badge.
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesUnread, setNotesUnread] = useState(0);
  const user = authService.getCurrentUser();

  // Light / dark theme (mobile shell only), persisted across sessions.
  const [theme, setTheme] = useState(() => localStorage.getItem(THEME_KEY) || 'dark');
  const toggleTheme = () => setTheme((t) => {
    const next = t === 'dark' ? 'light' : 'dark';
    try { localStorage.setItem(THEME_KEY, next); } catch (e) { /* ignore */ }
    return next;
  });

  // Barre du bas toujours visible — le masquage au scroll est retiré
  // (Ahmed 2026-08-16 : « footer ne se cache jamais »).

  // Bottom-bar action buttons (non-navigation): Paiement / Achat open the
  // shared sheet directly on the matching step.
  const handleAction = (action) => {
    if (action === 'paiement') setPaySheet('type');
    if (action === 'achat') setPaySheet('achat');
  };
  const tabs = getPrimaryTabs(user);
  const moreLinks = getMoreLinks(user);

  const handleLogout = () => {
    setMoreOpen(false);
    Swal.fire({
      title: 'Déconnexion',
      text: 'Voulez-vous vous déconnecter ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: KI.peacock,
      cancelButtonColor: KI.coralDark,
      confirmButtonText: 'Oui, déconnexion',
      cancelButtonText: 'Annuler',
    }).then((result) => {
      if (result.isConfirmed) {
        authService.logout().finally(() => navigate('/login'));
      }
    });
  };

  return (
    <div className="min-h-screen" data-mobile-theme={theme}>
      {/* Fixed page background (mobile shell only) — stays put while content scrolls.
          Dark = plain deep-dark surface ; Light = plain light surface. */}
      <div
        aria-hidden="true"
        className="fixed inset-0 -z-10"
        style={{ background: theme === 'light' ? KI.ramps.gray[100] : KI.darkBg }}
      />
      <MobileHeader onMenu={() => setMoreOpen(true)} badge={notesUnread} />

      <main className="pt-14 pb-20 min-h-screen">
        {/* Transition de page : même système que le shell desktop
            (index.css « Système de mouvement Ki Para »). La clé relance
            l'animation à chaque route ; le fond fixe, l'en-tête, la barre
            d'onglets et les sheets restent HORS de ce conteneur. */}
        <div key={pathname} className="kp-page">
          <RefreshableOutlet />
        </div>
        {/* Connectivity footer: WiFi light + grey status line. */}
        <ConnectivityFooter className="px-4 pt-6 pb-2" />
      </main>

      <BottomNav
        tabs={tabs}
        onMore={() => setMoreOpen(true)}
        onAction={handleAction}
        moreActive={moreOpen}
        hidden={false}
      />

      <MobilePaiementSheet open={!!paySheet} initialStep={paySheet || 'menu'} onClose={() => setPaySheet(null)} />

      <MobileNotesSheet
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        onUnreadChange={setNotesUnread}
      />

      <MoreSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        links={moreLinks}
        user={user}
        onLogout={handleLogout}
        theme={theme}
        onToggleTheme={toggleTheme}
        notesUnread={notesUnread}
        onNotes={() => { setMoreOpen(false); setNotesOpen(true); }}
      />
    </div>
  );
}
